
import React, { useState, useEffect } from 'react';
import { Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { triggerFavoriteAdd, triggerLikeAdd } from '@/utils/onboardingEvents';
import { useDebounce } from '@/hooks/useDebounce';

interface RecipeLikeButtonProps {
  recipeId: string;
  initialLikes?: number;
  initialLiked?: boolean;
  size?: 'sm' | 'default' | 'lg';
}

const LOCAL_STORAGE_LIKES_KEY = 'recipe-likes';

// Global state for batch like counts - shared across all RecipeLikeButton instances
let globalLikeCounts: Record<string, number> = {};
let globalUserLikes: Record<string, boolean> = {};
let globalLikesLoaded = false;

const RecipeLikeButton: React.FC<RecipeLikeButtonProps> = ({
  recipeId,
  initialLikes = 0,
  initialLiked = false,
  size = 'default',
}) => {
  const { user } = useAuth();
  const [isLiked, setIsLiked] = useState<boolean>(initialLiked);
  const [likeCount, setLikeCount] = useState<number>(initialLikes);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const debouncedRecipeId = useDebounce(recipeId, 300);

  // Load global likes data on first mount
  useEffect(() => {
    if (!globalLikesLoaded) {
      loadGlobalLikesData();
    }
  }, []);

  // Update local state when global data changes
  useEffect(() => {
    if (globalLikesLoaded && debouncedRecipeId) {
      setLikeCount(globalLikeCounts[debouncedRecipeId] || initialLikes);
      if (user?.id) {
        setIsLiked(globalUserLikes[`${user.id}_${debouncedRecipeId}`] || false);
      }
    }
  }, [debouncedRecipeId, user?.id, globalLikesLoaded, initialLikes]);

  const loadGlobalLikesData = async () => {
    try {
      console.log('Loading global likes data in batch...');
      
      // Load like counts using the existing RPC function
      const { data: likeCounts, error: countsError } = await supabase.rpc('get_recipe_like_counts');
      
      if (countsError) {
        console.error('Error fetching recipe like counts:', countsError);
      } else if (likeCounts) {
        // Convert to object for easy lookup
        globalLikeCounts = {};
        likeCounts.forEach((item: { recipe_id: string, count: number }) => {
          globalLikeCounts[item.recipe_id] = item.count;
        });
        console.log(`Loaded ${likeCounts.length} recipe like counts in batch`);
      }

      // Load user's likes if authenticated
      if (user?.id && !user.id.startsWith('user-')) {
        const { data: userLikes, error: userLikesError } = await supabase
          .from('recipe_likes')
          .select('recipe_id')
          .eq('user_id', user.id);
          
        if (userLikesError) {
          console.error('Error fetching user likes:', userLikesError);
        } else if (userLikes) {
          globalUserLikes = {};
          userLikes.forEach((like: { recipe_id: string }) => {
            globalUserLikes[`${user.id}_${like.recipe_id}`] = true;
          });
          console.log(`Loaded ${userLikes.length} user likes in batch`);
        }
      }
      
      // Handle localStorage for demo users
      if (user?.id?.startsWith('user-')) {
        const storedLikes = localStorage.getItem(LOCAL_STORAGE_LIKES_KEY);
        if (storedLikes) {
          try {
            const allLikes = JSON.parse(storedLikes);
            if (allLikes[user.id] && Array.isArray(allLikes[user.id])) {
              globalUserLikes = {};
              allLikes[user.id].forEach((recipeId: string) => {
                globalUserLikes[`${user.id}_${recipeId}`] = true;
              });
            }
          } catch (e) {
            console.error("Error parsing stored likes:", e);
          }
        }
      }
      
      globalLikesLoaded = true;
      
      // Trigger a global event to update all components
      window.dispatchEvent(new CustomEvent('global-likes-loaded'));
      
    } catch (error) {
      console.error('Error loading global likes data:', error);
    }
  };

  // Listen for global likes updates
  useEffect(() => {
    const handleGlobalLikesLoaded = () => {
      if (debouncedRecipeId) {
        setLikeCount(globalLikeCounts[debouncedRecipeId] || initialLikes);
        if (user?.id) {
          setIsLiked(globalUserLikes[`${user.id}_${debouncedRecipeId}`] || false);
        }
      }
    };

    const handleLikeUpdated = (event: CustomEvent) => {
      const { recipeId: updatedRecipeId, userId, isLiked: newIsLiked, count } = event.detail;
      if (updatedRecipeId === debouncedRecipeId) {
        globalLikeCounts[updatedRecipeId] = count;
        if (user?.id === userId) {
          globalUserLikes[`${userId}_${updatedRecipeId}`] = newIsLiked;
          setIsLiked(newIsLiked);
        }
        setLikeCount(count);
      }
    };

    window.addEventListener('global-likes-loaded', handleGlobalLikesLoaded);
    window.addEventListener('recipe-like-updated', handleLikeUpdated as EventListener);

    return () => {
      window.removeEventListener('global-likes-loaded', handleGlobalLikesLoaded);
      window.removeEventListener('recipe-like-updated', handleLikeUpdated as EventListener);
    };
  }, [debouncedRecipeId, user?.id, initialLikes]);

  const handleToggleLike = async () => {
    if (!user || !user.id) {
      toast.error('Debes iniciar sesión para dar like');
      return;
    }

    setIsLoading(true);
    console.log(`[LIKE] User ${user.id} toggling like for recipe ${debouncedRecipeId}`);

    try {
      // Optimistically update UI
      const newLiked = !isLiked;
      const newCount = newLiked ? likeCount + 1 : Math.max(0, likeCount - 1);
      
      setIsLiked(newLiked);
      setLikeCount(newCount);
      
      // Update global state
      globalLikeCounts[debouncedRecipeId] = newCount;
      globalUserLikes[`${user.id}_${debouncedRecipeId}`] = newLiked;
      
      if (user.id.startsWith('user-')) {
        // Handle mock user likes using localStorage
        let allLikes = {};
        const storedLikes = localStorage.getItem(LOCAL_STORAGE_LIKES_KEY);
        
        if (storedLikes) {
          try {
            allLikes = JSON.parse(storedLikes);
          } catch (e) {
            console.error("Error parsing stored likes:", e);
            allLikes = {};
          }
        }
        
        if (!allLikes[user.id]) {
          allLikes[user.id] = [];
        }
        
        const userLikes = allLikes[user.id];
        const likeIndex = userLikes.indexOf(debouncedRecipeId);
        
        if (newLiked && likeIndex === -1) {
          userLikes.push(debouncedRecipeId);
          localStorage.setItem(LOCAL_STORAGE_LIKES_KEY, JSON.stringify(allLikes));
          triggerLikeAdd();
        } else if (!newLiked && likeIndex !== -1) {
          userLikes.splice(likeIndex, 1);
          localStorage.setItem(LOCAL_STORAGE_LIKES_KEY, JSON.stringify(allLikes));
        }
      } else {
        // Handle real user likes with Supabase
        if (newLiked) {
          console.log('Adding like for recipe:', debouncedRecipeId, 'by user:', user.id);
          const { error } = await supabase
            .from('recipe_likes')
            .insert({
              recipe_id: debouncedRecipeId,
              user_id: user.id,
            });
            
          if (error) {
            console.error('Error adding like:', error);
            throw error;
          }
          
          triggerLikeAdd();
        } else {
          console.log('Removing like for recipe:', debouncedRecipeId, 'by user:', user.id);
          const { error } = await supabase
            .from('recipe_likes')
            .delete()
            .eq('recipe_id', debouncedRecipeId)
            .eq('user_id', user.id);
            
          if (error) {
            console.error('Error removing like:', error);
            throw error;
          }
        }
      }
      
      // Dispatch a like-updated event for other components to react to
      try {
        const likeEvent = new CustomEvent('recipe-like-updated', {
          detail: {
            recipeId: debouncedRecipeId,
            userId: user.id,
            isLiked: newLiked,
            count: newCount
          }
        });
        window.dispatchEvent(likeEvent);
      } catch (e) {
        console.error('Error dispatching like event:', e);
      }
      
    } catch (error) {
      console.error('Error toggling like:', error);
      // Revert optimistic update on error
      const revertedLiked = !isLiked;
      const revertedCount = revertedLiked ? likeCount + 1 : Math.max(0, likeCount - 1);
      
      setIsLiked(revertedLiked);
      setLikeCount(revertedCount);
      
      // Revert global state
      globalLikeCounts[debouncedRecipeId] = revertedCount;
      globalUserLikes[`${user.id}_${debouncedRecipeId}`] = revertedLiked;
      
      toast.error('Error al actualizar el like');
    } finally {
      setIsLoading(false);
    }
  };

  const iconSize = size === 'sm' ? 16 : size === 'lg' ? 24 : 20;
  const buttonSize = size === 'sm' ? 'sm' : size === 'lg' ? 'lg' : 'default';

  return (
    <div className="flex items-center gap-1 z-20">
      <Button
        variant="ghost"
        size={buttonSize}
        className={`${
          isLiked
            ? 'text-red-500 hover:text-red-600'
            : 'text-gray-400 hover:text-red-500'
        } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''} z-20 rounded-full bg-white/70 backdrop-blur-sm hover:bg-white/90`}
        onClick={handleToggleLike}
        disabled={isLoading || !user}
      >
        <Heart className={`${isLiked ? 'fill-current' : ''}`} size={iconSize} />
      </Button>
      <span className="text-sm font-medium">{likeCount}</span>
    </div>
  );
};

export default RecipeLikeButton;
